import { styled, css, keyframes, YakTheme } from 'next-yak'
import { Polymorphic, PolymorphicComponent, YakComponentOf, withPrivatePublicMap } from '@owl-nest/yak/extra-runtime'

import * as colors from '../constants/colors'
import * as fonts from '../constants/fonts'
import * as effects from '../constants/effects'
import * as icons from '../icons/glyphs'

import * as copy from './copy'
import * as spinner from './spinner'
import { cpSync } from 'fs'

const bounce = keyframes`
  30% {
    transform: scale(1.4);
  }

  50% {
    transform: scale(0.8);
  }

  100% {
    transform: scale(1);
  }
`

const debounce = keyframes`
  30% {
    transform: scale(0.8);
  }

  50% {
    transform: scale(0.7);
  }

  100% {
    transform: scale(1);
  }
`

export const unstyledButtonCss = css`
  ${copy.styles.M.light}
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;

  &:focus,
  &:hover,
  &:active {
    outline: none;
  }
`
export const UnstyledButton = styled(Polymorphic.button)`
  ${unstyledButtonCss}
` as YakComponentOf<PolymorphicComponent<'button'>>

export const Base = styled(Polymorphic.button)`
  align-items: center;
  border-radius: 2px;
  border: none;
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  font-family: ${fonts.PRIMARY_FONT};
  font-weight: 900;
  height: 50px;
  justify-content: center;
  outline: none;
  padding: 10px 40px;
  text-align: center;
  text-transform: uppercase;
  transition: 0.3s ease;
  text-decoration: none;

  &::-moz-focus-inner {
    border: 0;
  }

  &:focus {
    outline: none;
  }
` as YakComponentOf<PolymorphicComponent<'button'>>

export const Label = styled.span``

export const buttonColors = {
  black: { base: colors.PRIMARY_GREY_900, hover: colors.PRIMARY_GREY_700, hoverOutlined: colors.PRIMARY_GREY_900 },
  blue: { base: colors.PRIMARY_BLUE_500, hover: colors.PRIMARY_BLUE_700, hoverOutlined: colors.PRIMARY_BLUE_500 },
  green: {
    base: colors.SECONDARY_GREEN_500,
    // HACK: Was not replaced with a proper replacement during legacy colors deletion (was #64D088 before that).
    //  If you're seeing weird results, please reach out to Product to see if we shouldn't reintroduce #64D088 as a legacy token.
    hover: colors.SECONDARY_GREEN_800,
    hoverOutlined: colors.SECONDARY_GREEN_500,
  },
  grey: { base: colors.PRIMARY_GREY_500, text: colors.PRIMARY_GREY_000, hover: undefined, hoverOutlined: undefined },
  red: { base: colors.PRIMARY_RED_500, hover: colors.PRIMARY_RED_700, hoverOutlined: colors.PRIMARY_RED_500 },
}

export type ButtonProps = {
  /** @default 'blue */
  color?: keyof typeof buttonColors
  /** @default 'primary */
  kind?: 'primary' | 'secondary'
  /** @default false */
  loading?: boolean
  /** @default 'small */
  size?: 'small' | 'medium'
}

export const _disabledStyle = css`
  background-color: ${colors.PRIMARY_GREY_500};
  border: 2px solid ${colors.PRIMARY_GREY_500};
  color: ${colors.PRIMARY_GREY_000};
  cursor: not-allowed;
`

type PrivateButtonProps = { [K in keyof ButtonProps as `$${K}`]: ButtonProps[K] }

export function getPrimaryColor($color: PrivateButtonProps['$color'] = 'blue', theme?: YakTheme) {
  return (theme as any)?.colors?.primary ?? buttonColors[$color].base
}

function getHoverColor($color: PrivateButtonProps['$color'] = 'blue', theme?: YakTheme) {
  return (theme as any)?.colors?.hover ?? buttonColors[$color].hover ?? ''
}

const primaryButtonStyle = css<PrivateButtonProps>`
  background-color: ${({ $color, theme }) => getPrimaryColor($color, theme)};
  color: ${colors.PRIMARY_GREY_000};

  ${({ $loading }) =>
    !$loading &&
    css`
      &:hover {
        background-color: ${({ $color, theme }) => getHoverColor($color, theme)};
      }

      &:disabled {
        background-color: ${colors.PRIMARY_GREY_500};
      }
    `}
`

const secondaryButtonStyle = css<PrivateButtonProps>`
  background-color: transparent;
  border: 2px solid ${({ $color, theme }) => getPrimaryColor($color, theme)};
  color: ${({ $color, theme }) => getPrimaryColor($color, theme)};

  ${({ $loading }) =>
    !$loading &&
    css`
      &:hover {
        border: 2px solid ${({ $color, theme }) => getPrimaryColor($color, theme)};
        background-color: ${({ $color, theme }) => getPrimaryColor($color, theme)};
        color: ${colors.PRIMARY_GREY_000};
      }

      &:disabled {
        border: 2px solid ${colors.PRIMARY_GREY_500};
        color: ${colors.PRIMARY_GREY_500};
        background-color: transparent;
      }
    `}
`

const PrivateButton = styled(Base)<PrivateButtonProps>`
  font-size: 14px;
  line-height: 18px;

  &:disabled {
    ${({ $loading }) => !$loading && _disabledStyle}
  }

  ${({ $loading }) =>
    $loading &&
    css`
      cursor: wait;
    `};

  ${({ $kind = 'primary' }) => {
    if ($kind === 'primary') {
      return primaryButtonStyle
    } else if ($kind === 'secondary') {
      return secondaryButtonStyle
    }
  }}
`

export const Button = styled(
  withPrivatePublicMap(PrivateButton, ({ color, kind, loading, size, ...props }: ButtonProps) => ({
    ...props,
    $color: color,
    $kind: kind,
    $loading: loading,
    $size: size,
  })),
)`` as YakComponentOf<PolymorphicComponent<'button', ButtonProps>>

Button.displayName = 'S.button.Button'

export type CallToActionProps = {
  /** @default false */
  $onlyGlyph?: boolean
} & ButtonProps

export const CallToAction = styled(Button)<CallToActionProps>`
  width: 100%;

  ${icons.Icon} {
    height: 22px;
    ${({ $onlyGlyph = false }) =>
      !$onlyGlyph &&
      css`
        margin-right: 10px;
      `};
    width: 22px;
  }
` as YakComponentOf<PolymorphicComponent<'button', CallToActionProps>>

CallToAction.displayName = 'S.button.CallToAction'

export const EditButton = styled.button<{ $onlyGlyph?: boolean }>`
  ${copy._baseStyle};
  align-items: center;
  background-color: ${colors.BACKGROUND_ALERT};
  border-radius: 20px;
  border: 1px solid ${colors.SECONDARY_YELLOW_500};
  box-shadow: ${effects.SHADOW_5};
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 700;
  height: 40px;
  justify-content: center;
  padding: ${(props) => (props.$onlyGlyph ? '0px' : '0px 16px')};
  width: ${(props) => (props.$onlyGlyph ? '40px' : 'fit-content')};

  ${icons.Icon} {
    color: ${colors.SECONDARY_YELLOW_500};
    margin-right: ${(props) => (props.$onlyGlyph ? '0' : '8px')};
    margin-top: 1px;
  }

  &:hover {
    background:
      linear-gradient(0deg, rgba(233, 174, 90, 0.2) 0%, rgba(233, 174, 90, 0.2) 100%), ${colors.BACKGROUND_ALERT};
  }
`
EditButton.displayName = 'S.button.EditButton'

/** @deprecated use the `LinkAsButton` component instead */
export const TextButton = styled.button`
  cursor: pointer;
  background: transparent;
  border: none;
  color: ${colors.TEXT_TERTIARY};
  font-family: ${fonts.PRIMARY_FONT};
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
  margin: 0;
  padding: 13px;
  width: 100%;

  &:hover {
    text-decoration: underline;
  }
`
TextButton.displayName = 'S.button.TextButton'

type ActionButtonProps = { $active?: boolean; $hover?: boolean; $noShadow?: boolean }

export const ActionButton = styled(Base)<ActionButtonProps>`
  background-color: ${colors.PRIMARY_GREY_000};
  border-radius: 19px;
  color: ${colors.PRIMARY_SAND_900};
  font-size: 13px;
  font-weight: 700;
  height: 32px;
  padding: 0 15px;
  text-transform: uppercase;

  ${icons.Icon} {
    animation: ${({ $active }) => ($active ? debounce : bounce)} cubic-bezier(0.04, 0.4, 0.5, 0.95) 0.8s forwards 1;
    color: ${colors.PRIMARY_GREY_500};
    height: 17px;
    margin: 0 0 0 6px;
    transition: 0.3s ease;
    width: 17px;
  }

  ${({ $active, $hover }) => {
    if ($active && $hover) {
      return css`
        color: ${colors.PRIMARY_GREY_000};

        ${icons.Icon} {
          color: ${colors.PRIMARY_GREY_000};
        }

        background-color: ${colors.PRIMARY_RED_500};
      `
    } else if (($active && !$hover) || (!$active && $hover)) {
      return css`
        ${icons.Icon} {
          color: ${colors.PRIMARY_BLUE_500};
        }
      `
    }
  }};

  ${({ $noShadow = false }) => {
    if (!$noShadow) {
      return css`
        box-shadow: ${effects.SHADOW_1};
      `
    }
  }};
` as YakComponentOf<'button', ActionButtonProps>

ActionButton.displayName = 'S.button.ActionButton'

type RoundButtonProps = {
  $loading?: boolean
  $step: 'before' | 'during' | 'after'
  $unmounted: boolean
  $active?: boolean
  disabled?: boolean
  $fitToParent?: boolean
  $inverted?: boolean
}

export const RoundButton = styled(Base)<RoundButtonProps>`
  border-radius: 40px;
  border: 2px solid transparent;
  box-shadow: ${effects.SHADOW_3};
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 700;
  gap: 11px;
  height: 40px;
  line-height: 14px;
  padding: 10px 24px;
  position: relative;
  text-transform: none;
  white-space: nowrap;
  width: ${({ $fitToParent }) => ($fitToParent ? '100%' : 'auto')};

  ${Label} {
    opacity: ${({ $step, $unmounted }) => ($step !== 'during' || $unmounted ? 1 : 0)};
    transition: opacity 0.3s ease;
  }

  ${icons.Icon} {
    position: relative;
    top: 1px;
  }

  ${spinner.Spinner} {
    left: 50%;
    opacity: ${({ $step, $unmounted }) => ($step === 'during' && !$unmounted ? 1 : 0)};
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    transition: opacity 0.3s ease;
  }

  ${({ $inverted, theme, $active }) => {
    if ($active) {
      return css`
        background-color: ${colors.PRIMARY_GREY_000};
        border: 2px solid ${() => (theme as any)?.colors?.primary ?? colors.PRIMARY_BLUE_500};
        color: ${() => (theme as any)?.colors?.primary ?? colors.PRIMARY_BLUE_500};
        position: relative;

        &:focus {
          &:after {
            border-radius: 40px;
            border: 2px solid ${() => (theme as any)?.colors?.primary ?? colors.PRIMARY_BLUE_500};
            content: '';
            height: calc(100% - 6px);
            left: 1px;
            position: absolute;
            top: 1px;
            width: calc(100% - 6px);
          }
        }
      `
    }

    if ($inverted) {
      return css`
        background-color: ${colors.PRIMARY_GREY_000};
        color: ${() => (theme as any)?.colors?.primary ?? colors.PRIMARY_BLUE_500};
        position: relative;

        &:hover {
          background-color: ${() => (theme as any)?.colors?.primary ?? colors.PRIMARY_BLUE_500};
          color: ${colors.PRIMARY_GREY_000};
        }

        &:focus {
          background-color: ${() => (theme as any)?.colors?.primary ?? colors.PRIMARY_BLUE_500};
          color: ${colors.PRIMARY_GREY_000};
          box-shadow: 0 0 0 2px ${colors.PRIMARY_GREY_000} inset;
        }

        ${spinner.Dot} {
          background-color: ${() => (theme as any)?.colors?.primary ?? colors.PRIMARY_BLUE_500};
        }
      `
    }

    return css`
      background-color: ${() => (theme as any)?.colors?.primary ?? colors.PRIMARY_BLUE_500};
      color: ${colors.PRIMARY_GREY_000};

      &:hover {
        background-color: ${() => (theme as any)?.colors?.hover ?? colors.PRIMARY_BLUE_700};
        color: ${colors.PRIMARY_GREY_000};
      }

      &:focus {
        box-shadow: 0 0 0 2px ${colors.PRIMARY_GREY_000} inset;
      }

      ${spinner.Dot} {
        background-color: ${colors.PRIMARY_GREY_000};
      }
    `
  }};

  &:disabled {
    background-color: ${colors.PRIMARY_GREY_500};
    color: ${colors.PRIMARY_GREY_000};
    cursor: not-allowed;
  }
` as YakComponentOf<PolymorphicComponent<'button', RoundButtonProps>>
RoundButton.displayName = 'S.button.RoundButton'

export const LinkWrapper = styled.a<{ $disabled?: boolean }>`
  text-decoration: none;
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'enabled')};
`
LinkWrapper.displayName = 'S.button.LinkWrapper'

export const SliderButton = styled.button<{ $size: 'medium' | 'small' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  height: max-content;
  padding: 0;
  min-width: initial;
  background-color: ${colors.PRIMARY_GREY_000};
  border: 1px solid ${colors.PRIMARY_SAND_200};
  transition:
    background-color 0.2s ease-in-out,
    color 0.3s ease-in-out;

  ${({ $size }) => {
    if ($size === 'medium') {
      return css`
        min-height: 54px;
        width: 54px;
      `
    }

    return css`
      min-height: 40px;
      width: 40px;
    `
  }};

  &:hover {
    background-color: ${({ theme }) => (theme as any)?.colors?.primary ?? colors.PRIMARY_BLUE_500};

    ${icons.stroke.CaretLeft}, ${icons.stroke.CaretRight} {
      color: ${colors.PRIMARY_GREY_000};
    }
  }

  &:active,
  &:focus {
    outline: none;
  }

  ${icons.stroke.CaretLeft}, ${icons.stroke.CaretRight} {
    color: ${colors.PRIMARY_GREY_900};
  }

  &:disabled {
    background-color: ${colors.SECONDARY_GREY_400};
    cursor: not-allowed;

    ${icons.stroke.CaretLeft}, ${icons.stroke.CaretRight} {
      color: ${colors.PRIMARY_GREY_500};
    }
  }
`
SliderButton.displayName = 'S.button.SliderButton'
